import type {
	ExpansionWatchStatus,
	WatchedExpansionChangeDto,
	WatchedExpansionDto,
} from "@repo/game-admin"
import { format, parseISO } from "date-fns"
import { WATCH_STATUS_LABELS } from "./watch-status"

const SOURCE_NAMES: Record<string, string> = {
	import_source: "the import source",
	tcgcsv: "TCGplayer",
	manual: "hand",
	catalogue: "the expansion page",
}

/** A history entry to describe: a watcher change or a person's action. */
export interface WatchEventLike {
	type: WatchedExpansionChangeDto["type"]
	payload: Record<string, unknown> | null
	/** Null for what the watcher did on its own. */
	actorUserId?: string | null
}

function text(value: unknown): string | null {
	return typeof value === "string" && value !== "" ? value : null
}

function day(value: unknown): string {
	const date = text(value)
	return date ? format(parseISO(date), "MMM d, yyyy") : "no date"
}

function dateTime(value: unknown): string {
	const date = text(value)
	return date ? format(parseISO(date), "MMM d, HH:mm") : "no time"
}

function sourceName(value: unknown): string {
	const source = text(value)
	return source ? (SOURCE_NAMES[source] ?? source) : "a source"
}

function sourceList(value: unknown): string {
	const sources = Array.isArray(value) ? value.map(sourceName) : []
	return sources.length > 0 ? sources.join(" and ") : "its sources"
}

function dateMover(
	payload: Record<string, unknown>,
	byPerson: boolean,
): string {
	if (byPerson) return "by hand"
	if (payload.source !== "catalogue") return `by ${sourceName(payload.source)}`

	const by = text(payload.by)
	return by ? `on the expansion page by ${by}` : "on the expansion page"
}

function isStatus(value: string): value is ExpansionWatchStatus {
	return value in WATCH_STATUS_LABELS
}

function statusLabel(value: unknown): string {
	const status = text(value)
	return status && isStatus(status) ? WATCH_STATUS_LABELS[status] : "none"
}

/** One history entry in plain words, saying which source or person did it. */
export function describeEvent(event: WatchEventLike): string {
	const payload = event.payload ?? {}
	const byPerson = Boolean(event.actorUserId)

	switch (event.type) {
		case "created":
			return byPerson || payload.manual
				? "Added by hand"
				: `Found by ${sourceList(payload.sources)}`
		case "date_changed":
			return `Date moved ${day(payload.from)} → ${day(payload.to)} ${dateMover(
				payload,
				byPerson,
			)}`
		case "date_change_proposed":
			return `${capitalize(sourceName(payload.source))} proposes ${day(payload.to)} (was ${day(payload.from)})`
		case "date_change_approved":
			return `Approved ${day(payload.to)} from ${sourceName(payload.source)} (was ${day(payload.from)})`
		case "date_change_kept":
			return `Kept ${day(payload.from)}, turned down ${day(payload.to)} from ${sourceName(payload.source)}`
		case "readiness_changed":
			return `Status: ${statusLabel(payload.from)} → ${statusLabel(payload.to)}`
		case "no_longer_listed":
			return `No longer listed by ${sourceList(payload.sources)}`
		case "listed_again":
			return `Listed again by ${sourceList(payload.sources)}`
		case "kept_watching":
			return "Kept watching although no source lists it"
		case "date_follows_sources":
			return `Date handed back to ${sourceName(payload.source)}: ${day(payload.to)} (was ${day(payload.from)})`
		case "undone":
			return describeUndone(payload.undid, event.actorUserId ?? null)
		case "linked":
			return describeLink(payload)
		case "unlinked":
			return text(payload.from)
				? `Unlinked from ${text(payload.from)}`
				: "Unlinked"
		case "ignored":
			return payload.reason === "resolved"
				? `Merged into ${text(payload.expansionSlug) ?? "one of our expansions"}`
				: `Ignored${text(payload.reason) ? `: ${text(payload.reason)}` : ""}`
		case "unignored":
			return "Watching again"
		case "confirmed":
			return "Confirmed"
		case "rejected":
			return `Rejected${text(payload.reason) ? `: ${text(payload.reason)}` : ""}`
		case "edited":
			return payload.source === "catalogue"
				? `Code changed in the catalogue: ${text(payload.from) ?? "none"} → ${text(payload.to) ?? "none"}`
				: describeEdit(payload.fields)
		case "alerts_muted":
			return `Alerts muted until ${dateTime(payload.until)}${text(payload.reason) ? `: ${text(payload.reason)}` : ""}`
		case "alerts_unmuted":
			return payload.expired ? "Alerts resumed, mute ended" : "Alerts unmuted"
		default: {
			const _exhaustive: never = event.type
			return _exhaustive
		}
	}
}

const EVENT_TYPES: Record<WatchEventLike["type"], true> = {
	created: true,
	date_changed: true,
	readiness_changed: true,
	linked: true,
	unlinked: true,
	ignored: true,
	unignored: true,
	confirmed: true,
	rejected: true,
	edited: true,
	no_longer_listed: true,
	listed_again: true,
	date_change_proposed: true,
	date_change_approved: true,
	date_change_kept: true,
	kept_watching: true,
	date_follows_sources: true,
	undone: true,
	alerts_muted: true,
	alerts_unmuted: true,
}

function isEventType(value: unknown): value is WatchEventLike["type"] {
	return typeof value === "string" && value in EVENT_TYPES
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

function describeUndone(undid: unknown, actorUserId: string | null): string {
	if (!isRecord(undid) || !isEventType(undid.type) || undid.type === "undone") {
		return "Undid a change"
	}
	const payload = isRecord(undid.payload) ? undid.payload : null
	return `Undid: ${describeEvent({ type: undid.type, payload, actorUserId })}`
}

const FIELD_NAMES: Record<string, string> = {
	name: "name",
	code: "code",
	note: "note",
}

function describeEdit(fields: unknown): string {
	const names = Array.isArray(fields)
		? fields.flatMap((field) =>
				typeof field === "string" && FIELD_NAMES[field]
					? [FIELD_NAMES[field]]
					: [],
			)
		: []
	if (names.length === 0) return "Name or code edited"

	const listed =
		names.length === 1
			? names[0]
			: `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`
	return `${capitalize(listed ?? "")} edited`
}

function describeLink(payload: Record<string, unknown>): string {
	if (payload.mergedFrom !== undefined) {
		return `Merged ${text(payload.mergedName) ?? "another entry"} into this one`
	}

	const to = text(payload.to) ?? text(payload.expansionSlug)
	if (payload.catalogueRename === true) {
		return `Renamed in the catalogue: ${text(payload.from) ?? "?"} → ${to ?? "?"}`
	}
	if (payload.mergedInto !== undefined) {
		return to ? `Merged into ${to}` : "Merged into another watched expansion"
	}

	const from = text(payload.from)
	if (from && to) return `Link changed ${from} → ${to}`
	return to ? `Linked to ${to}` : "Linked"
}

function capitalize(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1)
}

/** Whether a watched expansion waits for a person to settle a watcher change. */
export function awaitsApproval(
	row: Pick<
		WatchedExpansionDto,
		"reviewState" | "pendingChange" | "noLongerListedAt"
	>,
): boolean {
	if (row.reviewState === "ignored" || row.reviewState === "rejected") {
		return false
	}
	return row.pendingChange !== null || row.noLongerListedAt !== null
}

/** The marker a row carries: waiting for approval, new or changed this week. */
export type ChangeMarker = "approve" | "new" | "changed"

/** A row's marker, the most pressing first; null when nothing to point out. */
export function changeMarker(
	row: Pick<
		WatchedExpansionDto,
		"reviewState" | "pendingChange" | "noLongerListedAt" | "recentChanges"
	>,
): ChangeMarker | null {
	if (awaitsApproval(row)) return "approve"
	if (row.recentChanges.length === 0) return null
	return row.recentChanges.every((change) => change.type === "created")
		? "new"
		: "changed"
}
